/**
 * Shared coordinate helpers: DPR dlPosition pipe format, generic DMS/decimals, validation.
 */

import type {Fields} from '../../modules/operations/core/_models'

/** e.g. Latitude: 19 Deg 07 min 30 sec N | Longitude: 120 Deg 30 min 00 sec E */
const DPR_SPLIT_RE =
  /Latitude:\s*(.+?)\s+Deg\s+(.+?)\s+min\s+(.+?)\s+sec\s+([NS])\s*\|\s*Longitude:\s*(.+?)\s+Deg\s+(.+?)\s+min\s+(.+?)\s+sec\s+([EW])/i

/** DEG min sec DIR (Fleet DPR fragments) */
const FRAGMENT_RE =
  /^(.+?)\s+Deg\s+(.+?)\s+min\s+(.+?)\s+sec\s+([NSEW])$/i

/** e.g. 120°30'00" E, 73°47' E */
const CLASSIC_DMS_RE =
  /^(-?\d+(?:\.\d+)?)\s*°(?:\s*(\d+(?:\.\d+)?)\s*['′])?(?:\s*(\d+(?:\.\d+)?)\s*["″])?\s*([NSEW])?$/i

/** e.g. "22 34 21.5 N" — space-separated DD MM SS.S Dir without symbols */
const SPACE_DMS_RE =
  /^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)\s+([NSEW])$/i

/** Bare DMS: "39 11 24 N" or "39 11 24.5 N" — Coastal Position Report format (no Deg/min/sec keywords) */
const BARE_DMS_RE =
  /^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)\s+([NSEW])$/i

function cleanDMS(input: string): string {
  return input
    .trim()
    .replace(/[′’´]/g, "'")
    .replace(/[″”]/g, '"')
    .replace(/\s+/g, ' ')
}

function normalizeOverflow(d: number, m: number, s: number): { deg: number; min: number; sec: number } {
  if (!isFinite(d) || !isFinite(m) || !isFinite(s)) return {deg: NaN, min: NaN, sec: NaN}

  let deg = d
  let min = m
  let sec = s
  if (sec >= 60) {
    min += Math.floor(sec / 60)
    sec = sec % 60
  }
  if (min >= 60) {
    deg += Math.floor(min / 60)
    min = min % 60
  }
  return {deg, min, sec}
}

function dmsTripleToSignedDecimal(
  degRaw: number,
  minRaw: number,
  secRaw: number,
  dir: string,
  axis: 'lat' | 'lon'
): number | null {
  const d = dir.toUpperCase()
  if (axis === 'lat' && d !== 'N' && d !== 'S') return null
  if (axis === 'lon' && d !== 'E' && d !== 'W') return null

  if (!isFinite(degRaw) || !isFinite(minRaw) || !isFinite(secRaw)) return null

  const ov = normalizeOverflow(Math.abs(degRaw), minRaw, secRaw)
  if (!isFinite(ov.deg) || !isFinite(ov.min) || !isFinite(ov.sec)) return null

  let decimal = ov.deg + ov.min / 60 + ov.sec / 3600
  if (axis === 'lat') decimal *= d === 'S' ? -1 : 1
  else decimal *= d === 'W' ? -1 : 1

  if (axis === 'lat' && (decimal < -90 || decimal > 90)) return null
  if (axis === 'lon' && (decimal < -180 || decimal > 180)) return null
  return decimal
}

/** Parses "12 Deg 34 min 56.7 sec N" style fragment only */
export function fragmentToDecimal(fragment: string, axis: 'lat' | 'lon'): number | null {
  const m = fragment.trim().match(FRAGMENT_RE)
  if (!m) return null
  const deg = parseFloat(String(m[1]).replace(',', '.'))
  const minPart = parseFloat(String(m[2]).replace(',', '.'))
  const sec = parseFloat(String(m[3]).replace(',', '.'))
  return dmsTripleToSignedDecimal(deg, minPart, sec, m[4], axis)
}

function parseClassicDmsString(s: string, axis: 'lat' | 'lon'): number | null {
  const m = s.trim().match(CLASSIC_DMS_RE)
  if (!m) return null
  const degRaw = parseFloat(m[1].replace(',', '.'))
  let minPart = m[2] !== undefined ? parseFloat(m[2].replace(',', '.')) : 0
  let secPart = m[3] !== undefined ? parseFloat(m[3].replace(',', '.')) : 0
  const dir = (m[4] || '').toUpperCase()

  if (!isFinite(degRaw) || !isFinite(minPart) || !isFinite(secPart)) return null

  const ov = normalizeOverflow(Math.abs(degRaw), minPart, secPart)
  if (!isFinite(ov.deg)) return null

  let decimal = ov.deg + ov.min / 60 + ov.sec / 3600

  let signSign = degRaw < 0 ? -1 : 1
  if (dir === 'S' || dir === 'W') signSign *= -1
  decimal *= signSign

  if (axis === 'lat' && (decimal < -90 || decimal > 90)) return null
  if (axis === 'lon' && (decimal < -180 || decimal > 180)) return null

  return decimal
}

/** Parses bare "DD MM SS DIR" format, e.g. "39 11 24 N" from Coastal Position Report */
function parseBareDmsString(s: string, axis: 'lat' | 'lon'): number | null {
  const m = s.trim().match(BARE_DMS_RE)
  if (!m) return null
  const deg = parseFloat(m[1].replace(',', '.'))
  const minPart = parseFloat(m[2].replace(',', '.'))
  const sec = parseFloat(m[3].replace(',', '.'))
  const dir = m[4].toUpperCase()
  return dmsTripleToSignedDecimal(deg, minPart, sec, dir, axis)
}

/**
 * Parses full DPR `dlPosition` string into signed decimal latitude / longitude (degrees).
 */
export function parseFleetDprDlPositionToDecimals(dlPosition: string | null | undefined): {lat: number; lon: number} | null {
  const raw = String(dlPosition ?? '').trim()
  if (!raw) return null
  const m = raw.match(DPR_SPLIT_RE)
  if (!m) return null
  const latFrag = `${m[1]} Deg ${m[2]} min ${m[3]} sec ${m[4]}`
  const lonFrag = `${m[5]} Deg ${m[6]} min ${m[7]} sec ${m[8]}`
  const lat = fragmentToDecimal(latFrag, 'lat')
  const lon = fragmentToDecimal(lonFrag, 'lon')
  if (lat == null || lon == null) return null
  return {lat, lon}
}

/** Single-axis parse: decimal "-120.123", classic DMS, Fleet DPR pipe string, Fleet fragment text. */
export function coordinateTextToDecimal(input: unknown, axis: 'lat' | 'lon'): number | null {
  if (input === null || input === undefined) return null
  if (typeof input === 'number' && Number.isFinite(input)) {
    if (axis === 'lat') return input >= -90 && input <= 90 ? input : null
    return input >= -180 && input <= 180 ? input : null
  }
  const s = cleanDMS(String(input))
  if (!s || s === '--:--') return null

  const pair = parseFleetDprDlPositionToDecimals(s)
  if (pair) return axis === 'lat' ? pair.lat : pair.lon

  const frag = fragmentToDecimal(s, axis)
  if (frag != null) return frag

  const classic = parseClassicDmsString(s, axis)
  if (classic != null) return classic

  // Try space-separated DD MM SS.S Dir ("22 34 21.5 N")
  const spaceDmsMatch = s.trim().match(SPACE_DMS_RE)
  if (spaceDmsMatch) {
    const deg = parseFloat(spaceDmsMatch[1].replace(',', '.'))
    const min = parseFloat(spaceDmsMatch[2].replace(',', '.'))
    const sec = parseFloat(spaceDmsMatch[3].replace(',', '.'))
    return dmsTripleToSignedDecimal(deg, min, sec, spaceDmsMatch[4], axis)
  }

  // Bare "DD MM SS DIR" from Coastal Position Report (e.g. "39 11 24 N")
  const bare = parseBareDmsString(s, axis)
  if (bare != null) return bare

  const plainDec = /^(-?\d+(?:\.\d+)?)°?$/.exec(s.trim())
  if (plainDec) {
    const val = parseFloat(plainDec[1].replace(',', '.'))
    if (!isFinite(val)) return null
    console.log(`[coordinateTextToDecimal] plainDecimal "${s}" → ${val}`)
    if (axis === 'lat' && val >= -90 && val <= 90) return val
    if (axis === 'lon' && val >= -180 && val <= 180) return val
  }

  console.log(`[coordinateTextToDecimal] NO MATCH for "${s}" axis=${axis}`)
  return null
}

/**
 * Converts signed decimal degrees to a DMS display string.
 * e.g. 22.57264 → "22° 34' 21.5 N",  -112.3639 → "112° 21' 50 W"
 */
export function decimalToDmsString(decimal: number, axis: 'lat' | 'lon', decimals = 1): string {
  if (!Number.isFinite(decimal)) return '—'
  const abs = Math.abs(decimal)
  const deg = Math.floor(abs)
  const minFloat = (abs - deg) * 60
  const min = Math.floor(minFloat)
  const sec = (minFloat - min) * 60
  const dir =
    axis === 'lat'
      ? (decimal >= 0 ? 'N' : 'S')
      : (decimal >= 0 ? 'E' : 'W')
  return `${deg}° ${min}' ${sec.toFixed(decimals)}" ${dir}`
}

export type CoordinateValidation = {
  valid: boolean
  /** Human-readable reason when invalid, e.g. "Minutes value (64) is outside the valid range (0–59)." */
  message?: string
}

const toNum = (x: string): number => parseFloat(String(x).replace(',', '.'))

/** Pull deg/min/sec/dir out of the common backend DMS formats (fragment, classic, space-separated). */
function extractDmsComponents(
  input: string,
  axis: 'lat' | 'lon'
): { deg: number; min: number; sec: number; dir: string } | null {
  const s = cleanDMS(input)

  // Whole DPR pipe string — pick the axis we care about
  const pipe = s.match(DPR_SPLIT_RE)
  if (pipe) {
    return axis === 'lat'
      ? {deg: toNum(pipe[1]), min: toNum(pipe[2]), sec: toNum(pipe[3]), dir: pipe[4]}
      : {deg: toNum(pipe[5]), min: toNum(pipe[6]), sec: toNum(pipe[7]), dir: pipe[8]}
  }

  // "18 Deg 64 min 34 sec N"
  let m = s.match(FRAGMENT_RE)
  if (m) return {deg: toNum(m[1]), min: toNum(m[2]), sec: toNum(m[3]), dir: m[4]}

  // "120°30'00\" E"
  m = s.match(CLASSIC_DMS_RE)
  if (m) {
    return {
      deg: toNum(m[1]),
      min: m[2] !== undefined ? toNum(m[2]) : 0,
      sec: m[3] !== undefined ? toNum(m[3]) : 0,
      dir: m[4] || '',
    }
  }

  // "18 64 34 N"
  m = s.match(SPACE_DMS_RE)
  if (m) return {deg: toNum(m[1]), min: toNum(m[2]), sec: toNum(m[3]), dir: m[4]}

  return null
}

/**
 * Validates a coordinate string exactly as received from the backend, WITHOUT normalizing
 * overflow. Returns the first range violation found so the UI can show the raw value with a
 * specific warning (e.g. minutes/seconds out of 0–59, degrees out of axis range).
 */
export function validateCoordinateString(input: unknown, axis: 'lat' | 'lon'): CoordinateValidation {
  if (input === null || input === undefined) return {valid: false, message: 'Coordinate is missing.'}
  const s = cleanDMS(String(input))
  if (!s || s === '--' || s === '--:--') return {valid: false, message: 'Coordinate is missing.'}

  const comp = extractDmsComponents(s, axis)
  if (!comp) {
    // Not a DMS string — accept a plain decimal that parses within range, else flag the format.
    if (coordinateTextToDecimal(s, axis) != null) return {valid: true}
    return {valid: false, message: 'Coordinate format is not recognized.'}
  }

  const {deg, min, sec, dir} = comp
  const label = axis === 'lat' ? 'Latitude' : 'Longitude'
  const maxDeg = axis === 'lat' ? 90 : 180

  if (!isFinite(deg) || !isFinite(min) || !isFinite(sec)) {
    return {valid: false, message: 'Coordinate contains a non-numeric value.'}
  }
  if (min < 0 || min >= 60) {
    return {valid: false, message: `Minutes value (${min}) is outside the valid range (0–59).`}
  }
  if (sec < 0 || sec >= 60) {
    return {valid: false, message: `Seconds value (${sec}) is outside the valid range (0–59.999).`}
  }
  if (deg < 0 || deg > maxDeg) {
    return {valid: false, message: `${label} degrees value (${deg}) is outside the valid range (0–${maxDeg}).`}
  }
  const u = (dir || '').toUpperCase()
  if (axis === 'lat' && u !== 'N' && u !== 'S') return {valid: false, message: 'Latitude direction must be N or S.'}
  if (axis === 'lon' && u !== 'E' && u !== 'W') return {valid: false, message: 'Longitude direction must be E or W.'}

  return {valid: true}
}

/** Allow digits + one decimal point while typing */
export function sanitizeCoordinatePart(raw: string): string {  const s = String(raw ?? '').replace(/[^\d.]/g, '')
  const i = s.indexOf('.')
  if (i === -1) return s
  return s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, '')
}

export type DprPositionFieldsLike = {
  startLatDeg: string
  startLatMin: string
  startLatSec: string
  startLatDir: 'N' | 'S'
  startLonDeg: string
  startLonMin: string
  startLonSec: string
  startLonDir: 'E' | 'W'
}

export type DprPositionFieldErrors = Partial<Record<keyof DprPositionFieldsLike, string>>

export const DPR_COORDINATE_ERROR_MESSAGES = {
  latDeg: 'Latitude degrees must be between 0 and 90.',
  lonDeg: 'Longitude degrees must be between 0 and 180.',
  min: 'Minutes must be between 0 and 59.',
  sec: 'Seconds must be between 0 and 59.999.',
  num: 'Enter a valid number for this field.',
  empty: 'This field is required.',
} as const

function parseNonNegNumber(raw: string): {ok: true; n: number} | {ok: false; err: string} {
  const t = String(raw ?? '').trim()
  if (t === '' || t === '--') return {ok: false, err: DPR_COORDINATE_ERROR_MESSAGES.empty}
  const n = parseFloat(t.replace(',', '.'))
  if (!isFinite(n) || n < 0) return {ok: false, err: DPR_COORDINATE_ERROR_MESSAGES.num}
  return {ok: true, n}
}

/**
 * Final validation for DPR position (submit). Range checks on blur-equivalent semantics.
 */
export function validateDprShipPositionFinal(fields: DprPositionFieldsLike): DprPositionFieldErrors {
  const E = DPR_COORDINATE_ERROR_MESSAGES
  const errors: DprPositionFieldErrors = {}

  const latDeg = parseNonNegNumber(fields.startLatDeg)
  const latMin = parseNonNegNumber(fields.startLatMin)
  const latSec = parseNonNegNumber(fields.startLatSec)
  const lonDeg = parseNonNegNumber(fields.startLonDeg)
  const lonMin = parseNonNegNumber(fields.startLonMin)
  const lonSec = parseNonNegNumber(fields.startLonSec)

  if (
    !latDeg.ok ||
    !latMin.ok ||
    !latSec.ok ||
    !lonDeg.ok ||
    !lonMin.ok ||
    !lonSec.ok
  ) {
    if (!latDeg.ok) errors.startLatDeg = latDeg.err
    if (!latMin.ok) errors.startLatMin = latMin.err
    if (!latSec.ok) errors.startLatSec = latSec.err
    if (!lonDeg.ok) errors.startLonDeg = lonDeg.err
    if (!lonMin.ok) errors.startLonMin = lonMin.err
    if (!lonSec.ok) errors.startLonSec = lonSec.err
    return errors
  }

  const ld = latDeg.n
  const lm = latMin.n
  const ls = latSec.n
  const xd = lonDeg.n
  const xm = lonMin.n
  const xs = lonSec.n

  if (ld > 90 || (ld === 90 && (lm > 0 || ls > 0))) errors.startLatDeg = E.latDeg
  if (xd > 180 || (xd === 180 && (xm > 0 || xs > 0))) errors.startLonDeg = E.lonDeg

  if (lm >= 60) errors.startLatMin = E.min
  if (xm >= 60) errors.startLonMin = E.min

  const maxSec = 59.999
  if (ls >= 60 || ls > maxSec + 1e-6) errors.startLatSec = E.sec
  if (xs >= 60 || xs > maxSec + 1e-6) errors.startLonSec = E.sec

  if (Object.keys(errors).length > 0) return errors

  const latDec = fragmentToDecimal(
    `${fields.startLatDeg.trim()} Deg ${fields.startLatMin.trim()} min ${fields.startLatSec.trim()} sec ${fields.startLatDir}`,
    'lat'
  )
  const lonDec = fragmentToDecimal(
    `${fields.startLonDeg.trim()} Deg ${fields.startLonMin.trim()} min ${fields.startLonSec.trim()} sec ${fields.startLonDir}`,
    'lon'
  )
  if (latDec == null) errors.startLatDeg = E.latDeg
  if (lonDec == null) errors.startLonDeg = E.lonDeg

  return errors
}

// --- Dynamic Operations reports (e.g. Noon at Sea): DMS fields by template label ---

export type GeoAxis = 'lat' | 'lon'
export type GeoDmsPart = 'deg' | 'min' | 'sec' | 'direction'

/** Same pattern as ReportStep geo grouping: lat/lon + deg|min|sec|direction token */
const TEMPLATE_GEO_PART_RE =
  /\b(?:deg(?:rees)?|mins?\b|minute|minutes|secs?\b|second|seconds|direction)\b/i

export function normalizeReportCoordinateLabel(label: string | undefined | null): string {
  return String(label ?? '')
    .toLowerCase()
    .replace(/postion/g, 'position')
    .replace(/\s+/g, ' ')
    .trim()
}

/** True for template fields like "NOON LONGITUDE MIN" or "Start Position Latitude Deg" */
export function isTemplateCoordinateDmsField(label: string | undefined | null): boolean {
  const l = normalizeReportCoordinateLabel(label)
  const hasCoord = /\blatitude\b/.test(l) || /\blongitude\b/.test(l)
  if (!hasCoord) return false
  return TEMPLATE_GEO_PART_RE.test(l)
}

export function classifyTemplateCoordinatePart(
  label: string | undefined | null
): {axis: GeoAxis; part: GeoDmsPart} | null {
  const l = normalizeReportCoordinateLabel(label)
  const isLat = /\blatitude\b/.test(l)
  const isLon = /\blongitude\b/.test(l)
  if (!isLat && !isLon) return null
  const axis: GeoAxis = isLat ? 'lat' : 'lon'

  const looksDirection =
    /\bdirection\b/.test(l) ||
    (/\bdir\b/i.test(l) &&
      !/\bdeg\b/.test(l) &&
      !/\bdegrees\b/.test(l) &&
      !/\bmin\b(?![a-z])/i.test(l) &&
      !/\bminute/.test(l) &&
      !/\bsec\b/i.test(l) &&
      !/\bsecond/.test(l))
  if (looksDirection) return {axis, part: 'direction'}
  if (/\bsec(?:ond)?s?\b/.test(l)) return {axis, part: 'sec'}
  if (/\bmins\b/.test(l) || /\bminute/.test(l) || /\bmin\b(?![a-z])/.test(l)) return {axis, part: 'min'}
  if (/\bdeg\b/.test(l) || /\bdegrees\b/.test(l)) return {axis, part: 'deg'}
  return null
}

export function friendlyCoordinatePartCaption(axis: GeoAxis, part: 'deg' | 'min' | 'sec'): string {
  const L = axis === 'lat' ? 'Latitude' : 'Longitude'
  if (part === 'deg') return `${L} degrees`
  if (part === 'min') return `${L} minutes`
  return `${L} seconds`
}

export const REPORT_COORDINATE_MESSAGES = {
  lonDegRange: 'Longitude degrees must be between 0 and 180.',
  latDegRange: 'Latitude degrees must be between 0 and 90.',
  lonMinRange: 'Longitude minutes must be between 0 and 59.',
  latMinRange: 'Latitude minutes must be between 0 and 59.',
  lonSecRange: 'Longitude seconds must be between 0 and 59.999.',
  latSecRange: 'Latitude seconds must be between 0 and 59.999.',
  latDirSel: 'Please select latitude direction.',
  lonDirSel: 'Please select longitude direction.',
  latDirNw: 'Latitude direction must be N or S.',
  lonDirEw: 'Longitude direction must be E or W.',
  num: 'Enter a valid non-negative number for this field.',
  latPoleComposite: 'At 90° latitude, minutes and seconds must be zero.',
  lonMeridianComposite: 'At 180° longitude, minutes and seconds must be zero.',
} as const

export function validateTemplateCoordinateNumericPart(params: {
  axis: GeoAxis
  part: 'deg' | 'min' | 'sec'
  raw: unknown
  required: boolean
}): string | null {
  const trimmed = String(params.raw ?? '').trim()
  const cap = friendlyCoordinatePartCaption(params.axis, params.part)
  const E = REPORT_COORDINATE_MESSAGES

  if (trimmed === '' || trimmed === '--') {
    if (params.required) return `${cap} are required.`
    return null
  }

  const n = parseFloat(trimmed.replace(',', '.'))
  if (!Number.isFinite(n) || n < 0) return E.num

  const {axis, part} = params

  if (part === 'deg') {
    if (axis === 'lat') {
      if (n > 90) return E.latDegRange
    } else if (n > 180) {
      return E.lonDegRange
    }
    return null
  }

  if (part === 'min') {
    if (n >= 60) return axis === 'lat' ? E.latMinRange : E.lonMinRange
    return null
  }

  if (part === 'sec') {
    const maxSec = 59.999
    if (n >= 60 || n > maxSec + 1e-6) return axis === 'lat' ? E.latSecRange : E.lonSecRange
    return null
  }

  return null
}

export function validateTemplateCoordinateDirection(params: {
  axis: GeoAxis
  raw: unknown
  required: boolean
}): string | null {
  const t = String(params.raw ?? '').trim()
  if (!t) {
    if (!params.required) return null
    return params.axis === 'lat' ? REPORT_COORDINATE_MESSAGES.latDirSel : REPORT_COORDINATE_MESSAGES.lonDirSel
  }
  const u = t.toUpperCase()
  if (params.axis === 'lat') {
    if (u !== 'N' && u !== 'S') return REPORT_COORDINATE_MESSAGES.latDirNw
  } else if (u !== 'E' && u !== 'W') {
    return REPORT_COORDINATE_MESSAGES.lonDirEw
  }
  return null
}

function extractReportCellValue(raw: Record<string, any>, fieldId: number): string {
  const cell = raw[fieldId]
  if (cell === undefined || cell === null) return ''
  if (typeof cell === 'object' && 'value' in cell && cell.value !== undefined && cell.value !== null) {
    return String(cell.value).trim()
  }
  return String(cell).trim()
}

type GeoBuckets = {
  lat: {start: Fields[]; stop: Fields[]; generic: Fields[]}
  lon: {start: Fields[]; stop: Fields[]; generic: Fields[]}
}

function bucketReportCoordinateFields(fields: Fields[]): GeoBuckets {
  const groups: GeoBuckets = {
    lat: {start: [], stop: [], generic: []},
    lon: {start: [], stop: [], generic: []},
  }

  fields.forEach((f) => {
    const l = normalizeReportCoordinateLabel(f.label)
    const isLat = /\blatitude\b/.test(l)
    const isLon = /\blongitude\b/.test(l)
    if (!(isLat || isLon)) return
    if (!TEMPLATE_GEO_PART_RE.test(l)) return

    const bucket = isLat ? groups.lat : groups.lon
    if (l.includes('start')) bucket.start.push(f)
    else if (l.includes('stop')) bucket.stop.push(f)
    else bucket.generic.push(f)
  })

  const orderKeys = ['deg', 'degree', 'min', 'minute', 'sec', 'second', 'direction']
  const sortFn = (a: Fields, b: Fields) => {
    const na = normalizeReportCoordinateLabel(a.label)
    const nb = normalizeReportCoordinateLabel(b.label)
    const ia = orderKeys.findIndex((k) => na.includes(k))
    const ib = orderKeys.findIndex((k) => nb.includes(k))
    return ia - ib
  }
  ;(['start', 'stop', 'generic'] as const).forEach((k) => {
    groups.lat[k].sort(sortFn)
    groups.lon[k].sort(sortFn)
  })

  return groups
}

function validateCoordinateClusterAxis(
  axis: GeoAxis,
  clusterFields: Fields[],
  values: Record<string, any>
): Record<number, string> {
  const out: Record<number, string> = {}
  if (!clusterFields.length) return out

  const vals: Partial<Record<GeoDmsPart, string>> = {}
  const ids: Partial<Record<GeoDmsPart, number>> = {}

  for (const f of clusterFields) {
    const c = classifyTemplateCoordinatePart(f.label)
    if (!c || c.axis !== axis) continue
    ids[c.part] = f.id
    vals[c.part] = extractReportCellValue(values, f.id)
  }

  if (ids.deg === undefined || ids.min === undefined || ids.sec === undefined || ids.direction === undefined) {
    return out
  }

  const d = (vals.deg ?? '').trim()
  const m = (vals.min ?? '').trim()
  const s = (vals.sec ?? '').trim()
  const dir = (vals.direction ?? '').trim()

  if (!d && !m && !s && !dir) return out

  const allFilled = d !== '' && m !== '' && s !== '' && dir !== ''
  if (!allFilled) return out

  const ld = parseFloat(d.replace(',', '.'))
  const lm = parseFloat(m.replace(',', '.'))
  const ls = parseFloat(s.replace(',', '.'))

  if (
    axis === 'lat' &&
    Number.isFinite(ld) &&
    Math.abs(ld - 90) < 1e-9 &&
    (lm > 0 || ls > 0)
  ) {
    out[ids.deg!] = REPORT_COORDINATE_MESSAGES.latPoleComposite
    return out
  }

  if (
    axis === 'lon' &&
    Number.isFinite(ld) &&
    Math.abs(ld - 180) < 1e-9 &&
    (lm > 0 || ls > 0)
  ) {
    out[ids.deg!] = REPORT_COORDINATE_MESSAGES.lonMeridianComposite
    return out
  }

  const frag = `${d} Deg ${m} min ${s} sec ${dir}`
  if (fragmentToDecimal(frag, axis === 'lat' ? 'lat' : 'lon') == null) {
    const mid = ids.deg ?? ids.min!
    out[mid] = axis === 'lat' ? REPORT_COORDINATE_MESSAGES.latDegRange : REPORT_COORDINATE_MESSAGES.lonDegRange
  }

  return out
}

/** Combine axis-level checks (90°/180° poles, numeric consistency) across all geo clusters on the step */
export function validateAllReportCoordinateClusters(
  formSections: Fields[],
  values: Record<string, any>
): Record<number, string> {
  const merged: Record<number, string> = {}
  const b = bucketReportCoordinateFields(formSections)

  const packs: Array<{axis: GeoAxis; fields: Fields[]}> = []
  if (b.lat.start.length) packs.push({axis: 'lat', fields: b.lat.start})
  else if (b.lat.generic.length) packs.push({axis: 'lat', fields: b.lat.generic})

  if (b.lat.stop.length) packs.push({axis: 'lat', fields: b.lat.stop})

  if (b.lon.start.length) packs.push({axis: 'lon', fields: b.lon.start})
  else if (b.lon.generic.length) packs.push({axis: 'lon', fields: b.lon.generic})

  if (b.lon.stop.length) packs.push({axis: 'lon', fields: b.lon.stop})

  for (const {axis, fields} of packs) {
    Object.assign(merged, validateCoordinateClusterAxis(axis, fields, values))
  }

  return merged
}

/* ───────────────────── Composite Position (Single Field DMS) ───────────────────── */

export interface CompositePositionParts {
  latDeg: string
  latMin: string
  latSec: string
  latDir: 'N' | 'S' | ''
  lonDeg: string
  lonMin: string
  lonSec: string
  lonDir: 'E' | 'W' | ''
}

export function parseCompositePositionValue(raw: any): CompositePositionParts {
  const empty: CompositePositionParts = {
    latDeg: '',
    latMin: '',
    latSec: '',
    latDir: 'N',
    lonDeg: '',
    lonMin: '',
    lonSec: '',
    lonDir: 'E',
  }

  if (raw === null || raw === undefined) return empty

  if (typeof raw === 'object') {
    if (raw.latDeg !== undefined || raw.lonDeg !== undefined) {
      return {
        latDeg: String(raw.latDeg ?? ''),
        latMin: String(raw.latMin ?? ''),
        latSec: String(raw.latSec ?? ''),
        latDir: raw.latDir === 'S' ? 'S' : 'N',
        lonDeg: String(raw.lonDeg ?? ''),
        lonMin: String(raw.lonMin ?? ''),
        lonSec: String(raw.lonSec ?? ''),
        lonDir: raw.lonDir === 'W' ? 'W' : 'E',
      }
    }
  }

  const str = String(raw).trim()
  if (!str || str === '--') return empty

  try {
    const parsed = JSON.parse(str)
    if (parsed && typeof parsed === 'object') {
      return {
        latDeg: String(parsed.latDeg ?? ''),
        latMin: String(parsed.latMin ?? ''),
        latSec: String(parsed.latSec ?? ''),
        latDir: parsed.latDir === 'S' ? 'S' : 'N',
        lonDeg: String(parsed.lonDeg ?? ''),
        lonMin: String(parsed.lonMin ?? ''),
        lonSec: String(parsed.lonSec ?? ''),
        lonDir: parsed.lonDir === 'W' ? 'W' : 'E',
      }
    }
  } catch {}

  const dprMatch = str.match(
    /(?:Latitude|Lat)?:?\s*([\d.]+)\s*(?:Deg|°)\s*([\d.]+)\s*(?:min|'|′)?\s*([\d.]+)\s*(?:sec|"|″)?\s*([NS])\s*(?:\||,|\/)?\s*(?:Longitude|Lon)?:?\s*([\d.]+)\s*(?:Deg|°)\s*([\d.]+)\s*(?:min|'|′)?\s*([\d.]+)\s*(?:sec|"|″)?\s*([EW])/i
  )
  if (dprMatch) {
    return {
      latDeg: dprMatch[1],
      latMin: dprMatch[2],
      latSec: dprMatch[3],
      latDir: dprMatch[4].toUpperCase() as 'N' | 'S',
      lonDeg: dprMatch[5],
      lonMin: dprMatch[6],
      lonSec: dprMatch[7],
      lonDir: dprMatch[8].toUpperCase() as 'E' | 'W',
    }
  }

  const latMatch = str.match(/(?:lat(?:itude)?:?\s*)?(\d+(?:\.\d+)?)\s*(?:°|deg)?\s*(\d+(?:\.\d+)?)?\s*(?:'|′|min)?\s*(\d+(?:\.\d+)?)?\s*(?:"|″|sec)?\s*([NS])/i)
  const lonMatch = str.match(/(?:lon(?:gitude)?:?\s*)?(\d+(?:\.\d+)?)\s*(?:°|deg)?\s*(\d+(?:\.\d+)?)?\s*(?:'|′|min)?\s*(\d+(?:\.\d+)?)?\s*(?:"|″|sec)?\s*([EW])/i)

  if (latMatch || lonMatch) {
    return {
      latDeg: latMatch ? latMatch[1] || '' : '',
      latMin: latMatch ? latMatch[2] || '' : '',
      latSec: latMatch ? latMatch[3] || '' : '',
      latDir: (latMatch ? (latMatch[4] || 'N').toUpperCase() : 'N') as 'N' | 'S',
      lonDeg: lonMatch ? lonMatch[1] || '' : '',
      lonMin: lonMatch ? lonMatch[2] || '' : '',
      lonSec: lonMatch ? lonMatch[3] || '' : '',
      lonDir: (lonMatch ? (lonMatch[4] || 'E').toUpperCase() : 'E') as 'E' | 'W',
    }
  }

  return empty
}

export function formatCompositePositionValue(parts: CompositePositionParts): string {
  const hasAnyLat = parts.latDeg || parts.latMin || parts.latSec
  const hasAnyLon = parts.lonDeg || parts.lonMin || parts.lonSec

  if (!hasAnyLat && !hasAnyLon) return ''

  const pad2 = (v?: string) => {
    const s = String(v ?? '').trim()
    if (!s) return '00'
    return s.length === 1 ? `0${s}` : s
  }

  const latDeg = parts.latDeg || '0'
  const latMin = pad2(parts.latMin)
  const latSec = pad2(parts.latSec)
  const latDir = parts.latDir || 'N'

  const lonDeg = parts.lonDeg || '0'
  const lonMin = pad2(parts.lonMin)
  const lonSec = pad2(parts.lonSec)
  const lonDir = parts.lonDir || 'E'

  return `Latitude: ${latDeg} Deg ${latMin} min ${latSec} sec ${latDir} | Longitude: ${lonDeg} Deg ${lonMin} min ${lonSec} sec ${lonDir}`
}

export function formatCoordinateForDisplay(raw: any): string {
  const parts = parseCompositePositionValue(raw)
  const hasLat = Boolean(parts.latDeg || parts.latMin || parts.latSec)
  const hasLon = Boolean(parts.lonDeg || parts.lonMin || parts.lonSec)
  if (!hasLat && !hasLon) {
    return String(raw ?? '--')
  }
  const pad2 = (val?: string) => {
    const s = String(val ?? '').trim()
    if (!s) return '00'
    return s.length === 1 ? `0${s}` : s
  }
  const latStr = `${parts.latDeg || '0'}° ${pad2(parts.latMin)}' ${pad2(parts.latSec)}" ${parts.latDir || 'N'}`
  const lonStr = `${parts.lonDeg || '0'}° ${pad2(parts.lonMin)}' ${pad2(parts.lonSec)}" ${parts.lonDir || 'E'}`

  if (hasLat && hasLon) {
    return `${latStr} | ${lonStr}`
  }
  return hasLat ? latStr : lonStr
}

